import { DEFAULTS } from '@auxcast/protocol';

/** Bounds of the latency slider, in milliseconds. */
export const MIN_JITTER_MS = 40;
export const MAX_JITTER_MS = DEFAULTS.maxJitterBufferMs;

export function clampJitterMs(ms: number): number {
  if (!Number.isFinite(ms)) return DEFAULTS.jitterBufferMs;
  return Math.round(Math.max(MIN_JITTER_MS, Math.min(MAX_JITTER_MS, ms)));
}

export interface JitterSizing {
  targetSamples: number;
  capSamples: number;
  capacitySamples: number;
}

/**
 * Turn a target latency into ring sizes at the AudioContext's sample rate.
 *
 * The ceiling sits above the target so ordinary network jitter is absorbed rather
 * than trimmed away on every burst, but never above `maxJitterBufferMs` — past that
 * the listener is so far behind that dropping the oldest audio is the kinder
 * outcome. Capacity is fixed for the largest buffer the UI can ask for so that
 * retuning never reallocates inside the audio thread.
 */
export function jitterSizing(targetMs: number, sampleRate: number): JitterSizing {
  const target = clampJitterMs(targetMs);
  const capMs = Math.min(MAX_JITTER_MS, Math.max(target * 2, target + 60));
  const samples = (ms: number) => Math.max(1, Math.ceil((sampleRate * ms) / 1000));
  return {
    targetSamples: samples(target),
    capSamples: samples(capMs),
    capacitySamples: samples(MAX_JITTER_MS + 250),
  };
}
